import type { FrameTimingSource } from './streamRecovery/contracts';

/**
 * Frame-latency meter (PRODUCT path, not a harness shim).
 *
 * Measures glass-to-glass video latency as the player experiences it: the time between the
 * host sending an encoded video frame and the guest decoding/presenting it. The two clocks
 * are different machines, so the absolute number includes clock skew — but for a game-stream
 * on a healthy link the skew is a few ms and the *trend* is what matters; a struggling link
 * shows up as both a higher floor and a growing spread.
 *
 * Host side: an insertable-streams transform on the video sender records
 * `rtpTimestamp → sendWallMs` for each encoded frame.
 * Guest side: `requestVideoFrameCallback` on the remote video element records
 * `rtpTimestamp → decodeWallMs`.
 * Correlation: the guest reports `(rtpTimestamp, decodeWallMs)` over the data channel;
 * the host looks up `sendWallMs` and computes `decodeWallMs - sendWallMs`.
 *
 * Safari note: rVFC on WebKit exposes no `rtpTimestamp`, so on Safari the meter falls back
 * to presented-frame timing only (decode wall time without RTP correlation) and reports
 * nothing it cannot defend. On Chromium/Android the full correlation runs.
 */

export type FrameSendRecord = {
  rtpTimestamp: number;
  sendWallMs: number;
};

export type FrameDecodeHint = {
  observationSequence?: number;
  observationAgeMs?: number;
  pathEpoch?: number;
  timingSource?: FrameTimingSource;
  rtpTimestamp: number;
  /** Composition-submission wall time for presentation-time hints; legacy callbacks otherwise. */
  decodeWallMs: number;
};

export type FrameLatencySummary = {
  /** Latest measured end-to-end latency, ms, or null before any correlation. */
  lastMs: number | null;
  /** Median of the recent window, or null. */
  p50Ms: number | null;
  /** 95th percentile of the recent window, or null. */
  p95Ms: number | null;
  /** Samples in the current window. */
  sampleCount: number;
};

/** How many send/decode records to keep for the rolling window. */
export const FRAME_LATENCY_WINDOW_SAMPLES = 120;

export class FrameLatencyTracker {
  private readonly sendByRtp = new Map<number, number>();
  private readonly recentLatencies: number[] = [];
  private readonly maxSendRecords: number;
  private readonly maxLatencySamples: number;

  constructor(opts: { maxSendRecords?: number; maxLatencySamples?: number } = {}) {
    this.maxSendRecords = opts.maxSendRecords ?? FRAME_LATENCY_WINDOW_SAMPLES;
    this.maxLatencySamples = opts.maxLatencySamples ?? FRAME_LATENCY_WINDOW_SAMPLES;
  }

  /** Host: record the wall time an encoded frame with this RTP timestamp was sent. */
  recordSend(rtpTimestamp: number, sendWallMs: number): void {
    this.sendByRtp.set(rtpTimestamp, sendWallMs);
    if (this.sendByRtp.size > this.maxSendRecords) {
      const oldest = this.sendByRtp.keys().next().value;
      if (oldest !== undefined) this.sendByRtp.delete(oldest);
    }
  }

  /**
   * Host: correlate a guest decode report with the recorded send. Returns the measured
   * latency in ms, or null when the frame's send time is unknown (too old, or the
   * tracker was just started).
   */
  measureFromDecodeHint(hint: FrameDecodeHint): number | null {
    const sendWallMs = this.sendByRtp.get(hint.rtpTimestamp);
    if (sendWallMs === undefined) return null;
    const latency = hint.decodeWallMs - sendWallMs;
    if (!Number.isFinite(latency) || latency < 0) return null;
    this.recentLatencies.push(latency);
    if (this.recentLatencies.length > this.maxLatencySamples) {
      this.recentLatencies.shift();
    }
    return latency;
  }

  /**
   * The host's recorded send wall time for an RTP timestamp, if still in the window.
   *
   * One-shot: the record is deleted on read so a replayed report for the same frame
   * cannot fabricate a second calibrated sample from it.
   */
  getSendWallMs(rtpTimestamp: number): number | undefined {
    const sendWallMs = this.sendByRtp.get(rtpTimestamp);
    if (sendWallMs !== undefined) this.sendByRtp.delete(rtpTimestamp);
    return sendWallMs;
  }

  /** Rolling summary for the debug overlay / beacon snapshot. */
  summary(): FrameLatencySummary {
    if (this.recentLatencies.length === 0) {
      return { lastMs: null, p50Ms: null, p95Ms: null, sampleCount: 0 };
    }
    const sorted = [...this.recentLatencies].sort((a, b) => a - b);
    const p50 = sorted[Math.floor((sorted.length - 1) * 0.5)];
    const p95 = sorted[Math.floor((sorted.length - 1) * 0.95)];
    return {
      lastMs: this.recentLatencies[this.recentLatencies.length - 1] ?? null,
      p50Ms: p50,
      p95Ms: p95,
      sampleCount: sorted.length,
    };
  }

  reset(): void {
    this.sendByRtp.clear();
    this.recentLatencies.length = 0;
  }
}
